import { useParams } from "react-router";
import { useState } from "react";
import { FirmDocumentsFolder } from "../models/FirmDocumentFolder/FirmDocumentsFolder";
import { DeleteFirmDocumentsFolderRequest } from "../models/Jobs/JobRequest";
import FirmDocumentFoldersTable from "../components/FirmDocumentFolders/FirmDocumentFoldersTable";
import ExportFirmDocumentsFoldersToCsvStatusTable from "../components/FirmDocumentFolders/ExportFirmDocumentsFoldersToCsvStatusTable";
import CreateExportFirmDocumentsFoldersToCsvTable from "../components/FirmDocumentFolders/CreateExportFirmDocumentsFoldersToCsvTable";
import DeleteFirmDocumentsFoldersTable from "../components/FirmDocumentFolders/DeleteFirmDocumentsFoldersTable";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {UserRole} from "../models/User";
import { Settings2, X } from "lucide-react";
import { useManagementDrawer } from "../hooks/useManagementDrawer";

export enum ActiveFirmDocumentsFolderJobsExplorer {
  DeleteFirmDocumentsFolders = 0,
  ExportCsv = 1,
}

interface FirmDocumentsFoldersPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

const FirmDocumentsFoldersPage: React.FC<FirmDocumentsFoldersPageProps> = ({
  userRole,
  isAssignedUser
}: FirmDocumentsFoldersPageProps) => {
  const { organizationId } = useParams();
  const [newFirmDocumentsFoldersForDeletion, setNewFirmDocumentsFoldersForDeletion,] = useState<DeleteFirmDocumentsFolderRequest[]>([]);
  const [newFirmDocumentsFoldersForExport, setNewFirmDocumentsFoldersForExport,] = useState<FirmDocumentsFolder[]>([]);
  const [selectedTab, setSelectedTab] = useState<ActiveFirmDocumentsFolderJobsExplorer>(ActiveFirmDocumentsFolderJobsExplorer.ExportCsv);
  const queuedCount = newFirmDocumentsFoldersForDeletion.length + newFirmDocumentsFoldersForExport.length;
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(queuedCount);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight">Firm Documents Folders</h2>
        {userRole !== UserRole.Viewer && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className="gap-2"
          >
            <Settings2 className="size-4" />
            Manage
            {queuedCount > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5">
                {queuedCount}
              </Badge>
            )}
          </Button>
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          <FirmDocumentFoldersTable
            organizationId={organizationId ?? ""}
            activeJobsExplorer={selectedTab}
            newFirmDocumentsFoldersForDeletion={newFirmDocumentsFoldersForDeletion}
            setNewFirmDocumentsFoldersForDeletion={setNewFirmDocumentsFoldersForDeletion}
            newFirmDocumentsFoldersForExport={newFirmDocumentsFoldersForExport}
            setNewFirmDocumentsFoldersForExport={setNewFirmDocumentsFoldersForExport}
            userRole={userRole}
            isAssignedUser={isAssignedUser}
          />
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage folders</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 p-4">
              <ToggleGroup
                type="single"
                value={String(selectedTab)}
                onValueChange={(v) => { if (v) setSelectedTab(Number(v) as ActiveFirmDocumentsFolderJobsExplorer); }}
                variant="outline"
                size="sm"
                className="w-full my-1"
              >
                {(userRole === UserRole.Admin || isAssignedUser) && (
                  <ToggleGroupItem value={String(ActiveFirmDocumentsFolderJobsExplorer.DeleteFirmDocumentsFolders)} className="flex-1">
                    Delete folders
                  </ToggleGroupItem>
                )}
                <ToggleGroupItem value={String(ActiveFirmDocumentsFolderJobsExplorer.ExportCsv)} className="flex-1">
                  Export CSV
                </ToggleGroupItem>
              </ToggleGroup>
              <div className="flex-1 min-h-0 flex flex-col">
                {selectedTab === ActiveFirmDocumentsFolderJobsExplorer.DeleteFirmDocumentsFolders && (userRole === UserRole.Admin || isAssignedUser) && (
                  <DeleteFirmDocumentsFoldersTable
                    organizationId={organizationId ?? ""}
                    newFirmDocumentsFoldersForDeletion={newFirmDocumentsFoldersForDeletion}
                    setNewFirmDocumentsFoldersForDeletion={setNewFirmDocumentsFoldersForDeletion}
                  />
                )}
                {selectedTab === ActiveFirmDocumentsFolderJobsExplorer.ExportCsv && (
                  <div className="flex-1 overflow-auto min-h-0">
                    <div className="flex flex-col h-full gap-4">
                      {newFirmDocumentsFoldersForExport.length > 0 && (
                        <>
                          <div className="flex items-center gap-3 py-2 pt-4">
                            <Separator className="flex-1" />
                            <span className="text-xs font-medium text-muted-foreground"><b>New</b></span>
                            <Separator className="flex-1" />
                          </div>
                          <CreateExportFirmDocumentsFoldersToCsvTable
                            organizationId={organizationId ?? ""}
                            newFirmDocumentsFoldersForExport={newFirmDocumentsFoldersForExport}
                            setNewFirmDocumentsFoldersForExport={setNewFirmDocumentsFoldersForExport}
                            userRole={userRole}
                            isAssignedUser={isAssignedUser}
                          />
                          <div className="flex items-center gap-3 py-2">
                            <Separator className="flex-1" />
                            <span className="text-xs font-medium text-muted-foreground"><b>Existing exports</b></span>
                            <Separator className="flex-1" />
                          </div>
                        </>
                      )}
                      <ExportFirmDocumentsFoldersToCsvStatusTable
                        organizationId={organizationId ?? ""}
                        userRole={userRole}
                        isAssignedUser={isAssignedUser}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirmDocumentsFoldersPage;
